import React, { useState } from 'react';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { DENY_REASONS, DENY_REASON_LABELS, getDenyReasonLabel, isOtherReason } from '~/consts/luxLoyalty';

import { useApproveRequest } from '~/hooks/apis/loyalty/useApproveRequest';
import { useGetApprovalRequest } from '~/hooks/apis/loyalty/useGetApprovalRequest';
import { useRejectRequest } from '~/hooks/apis/loyalty/useRejectRequest';

import type { ApprovalRequest, DenyReason, EvidenceField } from '~/services/LuxLoyalty';

import AppealChip from './AppealChip';
import ApprovalRequestStatusChip from './ApprovalRequestStatusChip';
import EvidenceReviewUserDetails from './EvidenceReviewUserDetails';
import { SectionCard } from './PayloadBuilder/SectionCard';
import UrlEvidenceValue, { isUrl } from './UrlEvidenceValue';

/** Tries to parse a JSON array of URLs from an evidence value (multi-file upload). */
function parseUrlArray(value: string): string[] | null {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string' && isUrl(v))) {
      return parsed;
    }
  } catch {
    // not JSON
  }
  return null;
}

type DecisionMode = null | 'approve' | 'reject';

interface EvidenceReviewDialogProps {
  request: ApprovalRequest;
  campaignId: string;
  evidenceFields: EvidenceField[];
  onClose: () => void;
}

export default function EvidenceReviewDialog({
  request,
  campaignId,
  evidenceFields,
  onClose,
}: EvidenceReviewDialogProps) {
  const [decision, setDecision] = useState<DecisionMode>(null);
  const [notes, setNotes] = useState('');
  const [denyReason, setDenyReason] = useState<DenyReason | ''>('');

  const approveMutation = useApproveRequest(campaignId);
  const rejectMutation = useRejectRequest(campaignId);
  const isPending = approveMutation.isPending || rejectMutation.isPending;
  const isPendingReview = request.status === 'PENDING';

  const { data: detailedRequest, isLoading: isLoadingDetail } = useGetApprovalRequest(request.id);

  const evidenceInput = detailedRequest?.evidenceInput ?? null;

  const handleApprove = () => {
    approveMutation.mutate({ requestId: request.id, notes: notes.trim() || undefined }, { onSuccess: onClose });
  };

  const handleReject = () => {
    if (!denyReason) return;
    rejectMutation.mutate(
      { requestId: request.id, denyReason, notes: notes.trim() || undefined },
      { onSuccess: onClose },
    );
  };

  const resetDecision = () => {
    setDecision(null);
    setNotes('');
    setDenyReason('');
  };

  const fieldDefs: Array<{ key: string; label: string; value: string }> | null =
    evidenceInput == null
      ? null
      : evidenceFields.length > 0
        ? evidenceFields.map((f) => ({
            key: f.name,
            label: f.label,
            value: String(evidenceInput[f.name] ?? ''),
          }))
        : Object.entries(evidenceInput).map(([key, value]) => ({
            key,
            label: key,
            value: String(value ?? ''),
          }));

  const rejectDisabled = isPending || !denyReason || (isOtherReason(denyReason) && !notes.trim());

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>Review Request</DialogTitle>

      <DialogContent dividers sx={{ flex: 1, overflow: 'auto' }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
          <ApprovalRequestStatusChip status={request.status} />
          <AppealChip attemptNumber={request.attemptNumber} />
          <Typography variant="body2" color="text.secondary">
            Submitted {new Date(request.createdAt).toLocaleString()}
          </Typography>
          {request.decidedBy && (
            <Typography variant="body2" color="text.secondary">
              Decided by <strong>{request.decidedBy}</strong>
            </Typography>
          )}
        </Stack>

        {request.denyReason && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography component="span" variant="body2" fontWeight="bold">
              Reason:
            </Typography>{' '}
            {getDenyReasonLabel(request.denyReason)}
            {request.notes && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {request.notes}
              </Typography>
            )}
          </Alert>
        )}

        {!request.denyReason && request.notes && (
          <Alert severity={request.status === 'REJECTED' ? 'error' : 'info'} sx={{ mb: 2 }}>
            {request.notes}
          </Alert>
        )}

        <EvidenceReviewUserDetails customerId={request.customerId} />

        <SectionCard title="Evidence" subtitle="Submitted loyalty program proof." defaultExpanded>
          {isLoadingDetail || !fieldDefs ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {fieldDefs.map(({ key, label, value }) => {
                const urls = parseUrlArray(value);
                return (
                  <Box key={key}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {label}
                    </Typography>
                    {urls ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {urls.map((url, i) => (
                          <UrlEvidenceValue key={i} value={url} label={`${label} ${i + 1}`} />
                        ))}
                      </Stack>
                    ) : isUrl(value) ? (
                      <UrlEvidenceValue value={value} label={label} />
                    ) : (
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {value || '—'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </>
          )}
        </SectionCard>

        {request.status === 'SUBMITTED' && (
          <Alert severity="info">
            This request is awaiting eligibility verification. Once the customer meets the booking criteria, it will
            move to <strong>Pending</strong> for your review.
          </Alert>
        )}

        {isPendingReview && decision === 'approve' && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {approveMutation.error && <Alert severity="error">{approveMutation.error.message}</Alert>}
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        )}

        {isPendingReview && decision === 'reject' && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {rejectMutation.error && <Alert severity="error">{rejectMutation.error.message}</Alert>}
            <TextField
              select
              label="Deny reason"
              value={denyReason}
              onChange={(e) => {
                const next = e.target.value as DenyReason;
                if (isOtherReason(denyReason) && !isOtherReason(next)) {
                  setNotes('');
                }
                setDenyReason(next);
              }}
              fullWidth
              required
            >
              <MenuItem value="" disabled>
                Select a reason…
              </MenuItem>
              {DENY_REASONS.map((reason) => (
                <MenuItem key={reason} value={reason}>
                  {DENY_REASON_LABELS[reason]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Additional notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
              fullWidth
              required={isOtherReason(denyReason)}
              helperText={isOtherReason(denyReason) ? 'Required when deny reason is "Other"' : 'Optional'}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          {isPendingReview && decision === null && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setDecision('approve')}
              >
                Approve
              </Button>
              <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => setDecision('reject')}>
                Reject
              </Button>
            </Stack>
          )}

          {isPendingReview && decision === 'approve' && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                onClick={handleApprove}
                disabled={isPending}
              >
                Confirm Approve
              </Button>
              <Button variant="text" onClick={resetDecision} disabled={isPending}>
                Back
              </Button>
            </Stack>
          )}

          {isPendingReview && decision === 'reject' && (
            <Stack direction="row" spacing={1}>
              <Tooltip
                title={
                  !denyReason
                    ? 'Select a deny reason to continue'
                    : isOtherReason(denyReason) && !notes.trim()
                      ? 'Notes are required when deny reason is "Other"'
                      : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
                    onClick={handleReject}
                    disabled={rejectDisabled}
                  >
                    Confirm Reject
                  </Button>
                </span>
              </Tooltip>
              <Button variant="text" onClick={resetDecision} disabled={isPending}>
                Back
              </Button>
            </Stack>
          )}
        </Box>

        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
